import equals from 'ramda/src/equals'
import difference from 'ramda/src/difference'
import isEmpty from 'ramda/src/isEmpty'
import conversationModule from '@/store/modules/conversation.module'
import templateModule from '@/store/modules/template.module'
import variablesModule from '@/store/modules/variables.module'
import { VARIABLE_REGEX_GLOBAL, VARIABLES_REGEX_SINGULAR } from '@/constants/variable-refs.constants'

class VariableRefsResolver {
  constructor(convJson) {
    this.currentVariableIds = this._findVariableIds(convJson, true)
  }

  deletes = () => variablesModule.variableRefs().deletes
  creates = () => variablesModule.variableRefs().creates

  resolveVariableRefs(convJson) {
    const variableIds = this._findVariableIds(convJson)
    if (equals(variableIds, this.currentVariableIds)) return

    const deletedVariableIds = this._findMissingVariableIds(variableIds)
    const newVariableIds = this._findNewVariableIds(variableIds)
    if (!isEmpty(deletedVariableIds)) this._resolveDeletes(deletedVariableIds)
    if (!isEmpty(newVariableIds)) this._resolveCreates(newVariableIds)

    this.currentVariableIds = variableIds
  }

  _findVariableIds(convJson, includeTemplateRefsDeletes = false) {
    const variableIdSet = new Set()
    this._findUniqueVariableIds(convJson, variableIdSet)

    if (!templateModule.isTemplateEditor()) {
      for (const [templateId, templateJson] of Object.entries(conversationModule.getJsons().templates)) {
        const templateRefsDeletes = templateModule.templateRefs().deletes.map(({ template_id }) => template_id)
        if (!includeTemplateRefsDeletes && templateRefsDeletes.includes(templateId)) continue

        this._findUniqueVariableIds(templateJson, variableIdSet)
      }
    }

    return Array.from(variableIdSet)
  }

  _findUniqueVariableIds(convJson, variableIdSet) {
    const stringedConvJson = JSON.stringify(convJson)
    const placeholders = stringedConvJson.match(VARIABLE_REGEX_GLOBAL) || []
    placeholders.forEach(placeholder => {
      const variableId = this._getVariableByName(placeholder.match(VARIABLES_REGEX_SINGULAR)[1])?.id
      if (variableId) variableIdSet.add(variableId)
    })
  }

  _resolveDeletes(deletedVariableIds) {
    const createsWithoutLocalDeletes = difference(this.creates(), deletedVariableIds)
    if (!equals(this.creates(), createsWithoutLocalDeletes)) {
      variablesModule.setVariableRefsCreates(createsWithoutLocalDeletes)
    } else if (!isEmpty(deletedVariableIds)) {
      variablesModule.setVariableRefsDeletes([...deletedVariableIds, ...this.deletes()])
    }
  }

  _resolveCreates(createdVariableIds) {
    const deletesWithoutLocalCreates = difference(this.deletes(), createdVariableIds)
    if (!equals(this.deletes(), deletesWithoutLocalCreates)) {
      variablesModule.setVariableRefsDeletes(deletesWithoutLocalCreates)
    } else if (!isEmpty(createdVariableIds)) {
      variablesModule.setVariableRefsCreates([...createdVariableIds, ...this.creates()])
    }
  }

  _findMissingVariableIds(newVariableIds) {
    return difference(this.currentVariableIds, newVariableIds)
  }

  _findNewVariableIds(newVariableIds) {
    const currentVariableIdsSet = new Set(this.currentVariableIds)
    const newVariableIdsSet = new Set(newVariableIds)
    return [...newVariableIdsSet].filter(value => !currentVariableIdsSet.has(value))
  }

  _getVariableByName(variableName) {
    const variables = variablesModule.getVariablesMergedWithBotScope()
    return variables.find(variable => variable.name === variableName)
  }
}

export default VariableRefsResolver
